import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  ThumbUp as ThumbUpIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  OpenInFull as OpenInFullIcon,
} from '@mui/icons-material';
import JoditEditor from 'jodit-react';
import { toast } from 'react-toastify';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';
import { usePermission } from 'modules/platform-components/utils/PermissionGate';
import { filterBySubjectScope } from 'modules/platform-components/utils/subject-scope';
import {
  apiPaths,
  getData,
  getInstutueApp,
  putInstituteAppData,
} from 'modules/platform-components/api-integration-util';

const getHeaders = () => {
  const user = getUserDetails();
  return { tenantId: user.tenantId, userEmail: user.email };
};

// ─── Conflict type colours ──────────────────────────────────────────
const conflictTypeConfig = {
  ALL_AGREE: { label: 'All Agree', color: '#22c55e', icon: <CheckCircleIcon fontSize="small" /> },
  DB_VS_AI: { label: 'DB vs AI', color: '#f59e0b', icon: <WarningIcon fontSize="small" /> },
  AI_DISAGREE: { label: 'AI Disagree', color: '#dc3545', icon: <ErrorIcon fontSize="small" /> },
  FULL_CONFLICT: { label: 'Full Conflict', color: '#dc3545', icon: <ErrorIcon fontSize="small" /> },
};

const statusConfig = {
  PROCESSING: { label: 'Processing...', color: '#8b5cf6' },
  PENDING: { label: 'Pending', color: '#f59e0b' },
  REVIEWED: { label: 'Reviewed', color: '#3366FF' },
  RESOLVED: { label: 'Resolved', color: '#22c55e' },
  DISMISSED: { label: 'Dismissed', color: '#8492a6' },
};

const providerColor = {
  OPENAI: '#10a37f',
  ANTHROPIC: '#d97706',
  GOOGLE_GEMINI: '#4285f4',
  UNKNOWN: '#666',
};

// ─── Stats Dashboard ────────────────────────────────────────────────
function StatsDashboard({ stats, onCardClick, activeFilter }) {
  if (!stats) return null;

  const cards = [
    { label: 'Total Verified', value: stats.total_verified, color: '#3366FF', filterKey: 'all' },
    { label: 'All Agree', value: stats.all_agree, color: '#22c55e', filterKey: 'type:ALL_AGREE' },
    { label: 'Conflicts Found', value: stats.conflicts_found, color: '#f59e0b', filterKey: 'conflicts' },
    { label: 'Pending Review', value: stats.pending_review, color: '#dc3545', filterKey: 'status:PENDING' },
    { label: 'Resolved', value: stats.resolved, color: '#22c55e', filterKey: 'status:RESOLVED' },
    { label: 'Answers Corrected', value: stats.answers_corrected, color: '#7c3aed', filterKey: 'corrected' },
    { label: 'Dismissed', value: stats.dismissed, color: '#8492a6', filterKey: 'status:DISMISSED' },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      {cards.map((card) => {
        const isActive = activeFilter === card.filterKey;
        return (
          <Paper key={card.label}
            onClick={() => onCardClick && card.value > 0 && onCardClick(card.filterKey)}
            sx={{
              p: 2, minWidth: 130, textAlign: 'center', borderTop: `3px solid ${card.color}`,
              cursor: onCardClick && card.value > 0 ? 'pointer' : 'default',
              outline: isActive ? `2px solid ${card.color}` : 'none',
              outlineOffset: -2,
              backgroundColor: isActive ? `${card.color}11` : 'inherit',
              transition: 'all 0.2s ease',
              '&:hover': onCardClick && card.value > 0 ? { boxShadow: 4, transform: 'translateY(-2px)' } : {},
            }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: card.color }}>{card.value || 0}</Typography>
            <Typography variant="caption" color="textSecondary">{card.label}</Typography>
          </Paper>
        );
      })}
    </Box>
  );
}

// ─── Panel Expand Dialog (read-only fullscreen view of one explanation) ──
function PanelExpandDialog({ open, onClose, label, provider, model, confidence, explanation }) {
  if (!explanation) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <span>{label} Explanation</span>
          {provider && (
            <Chip label={`${provider} / ${model || '?'}`} size="small" sx={{
              ml: 1, fontSize: 10, height: 20,
              backgroundColor: providerColor[provider] || '#666', color: '#fff'
            }} />
          )}
          {confidence != null && (
            <Chip label={`${(confidence * 100).toFixed(0)}% confidence`} size="small" variant="outlined" sx={{ ml: 0.5, fontSize: 10, height: 20 }} />
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <div dangerouslySetInnerHTML={{ __html: explanation }} style={{ fontSize: 14, lineHeight: 1.8 }} />
      </DialogContent>
    </Dialog>
  );
}

// ─── Explanation Panel (reusable for both dialogs) ──────────────────
function ExplanationPanel({ label, provider, model, confidence, explanation, selected, onUse }) {
  const [expandOpen, setExpandOpen] = useState(false);

  if (!explanation) return null;
  return (
    <>
      <Paper variant="outlined" sx={{
        p: 2, flex: 1, maxHeight: 250, overflow: 'auto',
        border: selected ? '2px solid #3366FF' : '1px solid #e0e0e0',
        position: 'relative',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
              {label}
            </Typography>
            {provider && (
              <Chip label={`${provider} / ${model || '?'}`} size="small" sx={{
                ml: 1, fontSize: 10, height: 18,
                backgroundColor: providerColor[provider] || '#666', color: '#fff'
              }} />
            )}
            {confidence != null && (
              <Chip label={`${(confidence * 100).toFixed(0)}%`} size="small" variant="outlined" sx={{ ml: 0.5, fontSize: 10, height: 18 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Expand">
              <IconButton size="small" onClick={() => setExpandOpen(true)}>
                <OpenInFullIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            {onUse && (
              <Button size="small" variant={selected ? "contained" : "outlined"} startIcon={<ContentCopyIcon />}
                sx={{ textTransform: 'none', fontSize: 11 }}
                onClick={onUse}>
                {selected ? 'Selected' : 'Use This'}
              </Button>
            )}
          </Box>
        </Box>
        {selected && (
          <Chip label="Currently selected" size="small" color="primary" sx={{ mb: 1, fontSize: 10, height: 18 }} />
        )}
        <div dangerouslySetInnerHTML={{ __html: explanation }} style={{ fontSize: 13, lineHeight: 1.6 }} />
      </Paper>
      <PanelExpandDialog
        open={expandOpen} onClose={() => setExpandOpen(false)}
        label={label} provider={provider} model={model} confidence={confidence} explanation={explanation}
      />
    </>
  );
}

// ─── Question Preview (reusable for both dialogs) ───────────────────
function QuestionPreview({ questionId }) {
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!questionId) return;
    setLoading(true);
    getData(apiPaths.questionbyid + `?id=${questionId}`)
      .then((res) => {
        if (res?.result) setQuestion(res.result);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [questionId]);

  if (loading) return <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /><Typography variant="caption" sx={{ ml: 1 }}>Loading question...</Typography></Box>;
  if (!question) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#fafafa' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Question</Typography>
      <div dangerouslySetInnerHTML={{ __html: question.question }} style={{ fontSize: 14, marginBottom: 12 }} />

      {question.answers && question.answers.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>Options</Typography>
          {question.answers.map((ans, idx) => (
            <Box key={idx} sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, px: 1,
              backgroundColor: ans.correct ? '#f0fdf4' : 'transparent',
              borderRadius: 1, mb: 0.5,
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 20 }}>
                {String.fromCharCode(65 + idx)}.
              </Typography>
              <div dangerouslySetInnerHTML={{ __html: ans.answer }} style={{ fontSize: 13 }} />
              {ans.correct && <Chip label="DB Correct" size="small" color="success" sx={{ fontSize: 10, height: 18, ml: 'auto' }} />}
            </Box>
          ))}
        </Box>
      )}

      {(question.ansExplanation || question.explanation) && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>Current Explanation</Typography>
          <div dangerouslySetInnerHTML={{ __html: question.ansExplanation || question.explanation }} style={{ fontSize: 12, color: '#555', marginTop: 4 }} />
        </Box>
      )}

      {(question.ansReference || question.reference) && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>Reference</Typography>
          <div dangerouslySetInnerHTML={{ __html: question.ansReference || question.reference }} style={{ fontSize: 12, color: '#555', marginTop: 4 }} />
        </Box>
      )}
    </Paper>
  );
}

// ─── Detail Dialog (read-only, for viewing RESOLVED/ALL_AGREE/DISMISSED) ──
function DetailDialog({ open, conflict, onClose, onSwitchExplanation, onCorrectAnswer, onRefresh }) {
  const canUpdateExplanation = usePermission('QA Actions', 'Update Explanation');
  const [switching, setSwitching] = useState(false);
  const [activeExplanation, setActiveExplanation] = useState(null);
  const [editedExplanation, setEditedExplanation] = useState('');
  const [selectedSource, setSelectedSource] = useState(null);
  const [showAnswerCorrection, setShowAnswerCorrection] = useState(false);
  const [correctedAnswer, setCorrectedAnswer] = useState('');
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [correcting, setCorrecting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandFinalExpl, setExpandFinalExpl] = useState(false);
  const detailEditorRef = useRef(null);

  useEffect(() => {
    if (conflict) {
      setActiveExplanation(conflict.resolvedExplanation);
      setEditedExplanation(conflict.resolvedExplanation || '');
      setSelectedSource(null);
      setCorrectedAnswer(conflict.resolvedAnswer || conflict.dbAnswer || '');
      setShowAnswerCorrection(false);
      setCorrectionNotes('');
    }
  }, [conflict]);

  if (!conflict) return null;

  const isResolved = conflict.status === 'RESOLVED';
  const currentAnswer = conflict.resolvedAnswer || conflict.dbAnswer;

  const handleUseExplanation = (explanation, source, answer) => {
    setEditedExplanation(explanation);
    setSelectedSource(source);
    if (answer) setCorrectedAnswer(answer);
  };

  const handleSaveExplanation = async () => {
    if (!editedExplanation?.trim()) {
      toast.error('Explanation cannot be empty');
      return;
    }
    setSwitching(true);
    try {
      const answerChanged = correctedAnswer && correctedAnswer !== currentAnswer;
      if (answerChanged) {
        // Answer changed — use resolve endpoint for both answer + explanation + audit
        await onCorrectAnswer(conflict.id, correctedAnswer, editedExplanation, 'Answer and explanation updated');
        toast.success('Answer changed to ' + correctedAnswer + ' and explanation updated');
      } else {
        // Only explanation changed
        await onSwitchExplanation(conflict.questionId, editedExplanation, selectedSource || 'edited');
        toast.success('Explanation updated');
      }
      setActiveExplanation(editedExplanation);
      setSelectedSource(null);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Failed to save: ' + (e.message || e));
    } finally {
      setSwitching(false);
    }
  };

  const handleCorrectAnswer = async () => {
    if (!correctedAnswer) {
      toast.error('Please select the correct answer');
      return;
    }
    if (correctedAnswer === currentAnswer) {
      toast.info('Same answer selected — no change needed');
      setShowAnswerCorrection(false);
      return;
    }
    setCorrecting(true);
    try {
      await onCorrectAnswer(conflict.id, correctedAnswer, activeExplanation || '', correctionNotes);
      toast.success('Answer corrected from ' + currentAnswer + ' to ' + correctedAnswer);
      setShowAnswerCorrection(false);
      if (onRefresh) onRefresh();
      onClose();
    } catch (e) {
      toast.error('Failed to correct answer: ' + (e.message || e));
    } finally {
      setCorrecting(false);
    }
  };

  const answerOptions = ['A', 'B', 'C', 'D'];

  return (
    // disableEnforceFocus: Jodit renders its image/video/link popups into
    // document.body, outside this dialog. MUI's FocusTrap pulls focus back into
    // the modal root on every focusin, so without this the popups' URL inputs
    // can be clicked but never typed into.
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isFullscreen} disableEnforceFocus>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Verification Details — Question #{conflict.questionId}</span>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {conflictTypeConfig[conflict.conflictType] && (
            <Chip
              icon={conflictTypeConfig[conflict.conflictType].icon}
              label={conflictTypeConfig[conflict.conflictType].label}
              size="small"
              sx={{ backgroundColor: conflictTypeConfig[conflict.conflictType].color, color: '#fff' }}
            />
          )}
          {statusConfig[conflict.status] && (
            <Chip label={statusConfig[conflict.status].label} size="small" variant="outlined"
              sx={{ borderColor: statusConfig[conflict.status].color, color: statusConfig[conflict.status].color }} />
          )}
          <Chip
            label={conflict.source === 'BATCH' ? 'Batch' : 'Manual'}
            size="small"
            variant="outlined"
            sx={{
              borderColor: conflict.source === 'BATCH' ? '#8b5cf6' : '#3366FF',
              color: conflict.source === 'BATCH' ? '#8b5cf6' : '#3366FF'
            }}
          />
          <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={() => setIsFullscreen(f => !f)}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Question context */}
        <QuestionPreview questionId={conflict.questionId} />

        {/* Answer comparison */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Answer Comparison</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Source</strong></TableCell>
                <TableCell><strong>Answer</strong></TableCell>
                <TableCell><strong>Confidence</strong></TableCell>
                <TableCell><strong>Reasoning</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Database" size="small" /></TableCell>
                <TableCell><strong style={{ fontSize: 16 }}>{conflict.dbAnswer}</strong></TableCell>
                <TableCell>—</TableCell>
                <TableCell>Stored correct answer</TableCell>
              </TableRow>
              {conflict.primaryAnswer && (
                <TableRow>
                  <TableCell>
                    <Chip label={`Primary: ${conflict.primaryProvider}`} size="small"
                      sx={{ backgroundColor: providerColor[conflict.primaryProvider] || '#666', color: '#fff' }} />
                  </TableCell>
                  <TableCell>
                    <strong style={{ fontSize: 16, color: conflict.primaryAnswer === conflict.dbAnswer ? '#22c55e' : '#dc3545' }}>
                      {conflict.primaryAnswer}
                    </strong>
                  </TableCell>
                  <TableCell>{conflict.primaryConfidence ? (conflict.primaryConfidence * 100).toFixed(0) + '%' : '—'}</TableCell>
                  <TableCell>{conflict.primaryReasoning || '—'}</TableCell>
                </TableRow>
              )}
              {conflict.secondaryAnswer && (
                <TableRow>
                  <TableCell>
                    <Chip label={`Secondary: ${conflict.secondaryProvider}`} size="small"
                      sx={{ backgroundColor: providerColor[conflict.secondaryProvider] || '#666', color: '#fff' }} />
                  </TableCell>
                  <TableCell>
                    <strong style={{ fontSize: 16, color: conflict.secondaryAnswer === conflict.dbAnswer ? '#22c55e' : '#dc3545' }}>
                      {conflict.secondaryAnswer}
                    </strong>
                  </TableCell>
                  <TableCell>{conflict.secondaryConfidence ? (conflict.secondaryConfidence * 100).toFixed(0) + '%' : '—'}</TableCell>
                  <TableCell>{conflict.secondaryReasoning || '—'}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Explanations with "Use This" buttons for resolved records */}
        {(conflict.primaryExplanation || conflict.secondaryExplanation) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              AI Explanations {isResolved && '— click "Use This" to load into the editor below'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <ExplanationPanel
                label="Primary"
                provider={conflict.primaryProvider}
                model={conflict.primaryModel}
                confidence={conflict.primaryConfidence}
                explanation={conflict.primaryExplanation}
                selected={selectedSource === 'Primary'}
                onUse={isResolved ? () => handleUseExplanation(conflict.primaryExplanation, 'Primary', conflict.primaryAnswer) : null}
              />
              <ExplanationPanel
                label="Secondary"
                provider={conflict.secondaryProvider}
                model={conflict.secondaryModel}
                confidence={conflict.secondaryConfidence}
                explanation={conflict.secondaryExplanation}
                selected={selectedSource === 'Secondary'}
                onUse={isResolved ? () => handleUseExplanation(conflict.secondaryExplanation, 'Secondary', conflict.secondaryAnswer) : null}
              />
            </Box>
          </Box>
        )}

        {/* Select Correct Answer — always visible for resolved records */}
        {isResolved && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Select Correct Answer
              <span style={{ fontWeight: 'normal', marginLeft: 8, color: '#888', fontSize: 12 }}>
                (DB: {conflict.dbAnswer}
                {conflict.primaryAnswer && ` | Primary: ${conflict.primaryAnswer}`}
                {conflict.secondaryAnswer && ` | Secondary: ${conflict.secondaryAnswer}`})
              </span>
            </Typography>
            <RadioGroup row value={correctedAnswer} onChange={(e) => setCorrectedAnswer(e.target.value)}>
              {['A', 'B', 'C', 'D'].map((opt) => (
                <FormControlLabel key={opt} value={opt} control={<Radio size="small" />}
                  label={<span style={{ fontWeight: opt === correctedAnswer ? 'bold' : 'normal' }}>
                    {opt} {opt === currentAnswer && '(current)'}
                  </span>}
                />
              ))}
            </RadioGroup>
          </Box>
        )}

        {/* Editable final explanation — same Jodit editor as ResolveDialog */}
        {isResolved && canUpdateExplanation && (
          <Paper variant="outlined" sx={{ mt: 1, mb: 2, p: 2, border: '2px solid #3366FF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                  Final Explanation
                </Typography>
                {selectedSource && (
                  <Chip label={`Based on ${selectedSource}`} size="small" color="primary" sx={{ ml: 1, fontSize: 10, height: 18 }} />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Tooltip title="Edit in fullscreen">
                  <IconButton size="small" onClick={() => setExpandFinalExpl(true)}>
                    <OpenInFullIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Button size="small" variant="contained" onClick={handleSaveExplanation} disabled={switching}
                  sx={{ textTransform: 'none' }}>
                  {switching ? <CircularProgress size={16} /> : 'Save Explanation'}
                </Button>
              </Box>
            </Box>
            <JoditEditor
              ref={detailEditorRef}
              value={editedExplanation}
              config={{
                readonly: false,
                askBeforePasteHTML: false,
                askBeforePasteFromWord: false,
                height: 250,
                buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,table,|,undo,redo,|,source',
              }}
              onBlur={(newContent) => {
                const val = typeof newContent === 'string' ? newContent : newContent?.target?.innerHTML || '';
                setEditedExplanation(val);
                setSelectedSource(null);
              }}
            />
          </Paper>
        )}

        {/* Fullscreen editor for Final Explanation */}
        <Dialog open={expandFinalExpl} onClose={() => setExpandFinalExpl(false)} maxWidth="lg" fullWidth disableEnforceFocus>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Final Explanation — Edit</span>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button size="small" variant="contained" sx={{ textTransform: 'none' }}
                onClick={() => { handleSaveExplanation(); setExpandFinalExpl(false); }}
                disabled={switching || !editedExplanation?.trim()}>
                {switching ? <CircularProgress size={16} /> : 'Save Explanation'}
              </Button>
              <IconButton size="small" onClick={() => setExpandFinalExpl(false)}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <JoditEditor
              value={editedExplanation}
              config={{
                readonly: false,
                askBeforePasteHTML: false,
                askBeforePasteFromWord: false,
                height: 500,
                buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,table,|,undo,redo,|,source',
              }}
              onBlur={(newContent) => {
                const val = typeof newContent === 'string' ? newContent : newContent?.target?.innerHTML || '';
                setEditedExplanation(val);
                setSelectedSource(null);
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Resolution info */}
        {isResolved && conflict.resolvedBy && (
          <Alert severity={conflict.resolvedBy === 'SYSTEM_AUTO_CONTENT_PRESERVED' ? 'warning' : 'info'} sx={{ mt: 1 }}>
            Resolved by <strong>{conflict.resolvedBy}</strong> with answer <strong>{currentAnswer}</strong>
            {conflict.resolvedAt && ` on ${new Date(conflict.resolvedAt).toLocaleString()}`}
            {conflict.source && <> — Source: <strong>{conflict.source === 'BATCH' ? 'Batch Processing' : 'Manual Trigger'}</strong></>}
            {conflict.resolvedBy === 'SYSTEM_AUTO_CONTENT_PRESERVED' && (
              <Chip label="Images/Tables Preserved" size="small" color="warning"
                sx={{ ml: 1, fontSize: 10, height: 20, fontWeight: 'bold' }} />
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {switching && <CircularProgress size={20} sx={{ mr: 1 }} />}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Resolution Dialog (for PENDING conflicts) ─────────────────────
function ResolveDialog({ open, conflict, onClose, onResolve }) {
  const [resolvedAnswer, setResolvedAnswer] = useState('');
  const [resolvedExplanation, setResolvedExplanation] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandFinalExpl, setExpandFinalExpl] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (conflict) {
      // Pre-select the explanation and answer from the same source
      if (conflict.primaryExplanation) {
        setResolvedExplanation(conflict.primaryExplanation);
        setResolvedAnswer(conflict.primaryAnswer || conflict.dbAnswer || '');
        setSelectedSource('primary');
      } else if (conflict.secondaryExplanation) {
        setResolvedExplanation(conflict.secondaryExplanation);
        setResolvedAnswer(conflict.secondaryAnswer || conflict.dbAnswer || '');
        setSelectedSource('secondary');
      } else {
        setResolvedExplanation('');
        setResolvedAnswer(conflict.dbAnswer || '');
        setSelectedSource(null);
      }
      setNotes('');
    }
  }, [conflict]);

  const handleUseExplanation = (explanation, source, answer) => {
    setResolvedExplanation(explanation);
    setSelectedSource(source);
    if (answer) {
      setResolvedAnswer(answer);
    }
  };

  const handleResolve = async () => {
    if (!resolvedAnswer) {
      toast.error('Please select the correct answer');
      return;
    }
    setSaving(true);
    try {
      await onResolve(conflict.id, resolvedAnswer, resolvedExplanation, notes);
      onClose();
    } catch (e) {
      toast.error('Failed to resolve: ' + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  if (!conflict) return null;

  const answerOptions = ['A', 'B', 'C', 'D'];

  return (
    // disableEnforceFocus: Jodit renders its image/video/link popups into
    // document.body, outside this dialog. MUI's FocusTrap pulls focus back into
    // the modal root on every focusin, so without this the popups' URL inputs
    // can be clicked but never typed into.
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isFullscreen} disableEnforceFocus>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Resolve Conflict — Question #{conflict.questionId}</span>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={conflict.source === 'BATCH' ? 'Batch' : 'Manual'}
            size="small"
            variant="outlined"
            sx={{
              borderColor: conflict.source === 'BATCH' ? '#8b5cf6' : '#3366FF',
              color: conflict.source === 'BATCH' ? '#8b5cf6' : '#3366FF'
            }}
          />
          <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={() => setIsFullscreen(f => !f)}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Question context */}
        <QuestionPreview questionId={conflict.questionId} />

        {/* Source comparison table */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Answer Comparison</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Source</strong></TableCell>
                <TableCell><strong>Answer</strong></TableCell>
                <TableCell><strong>Confidence</strong></TableCell>
                <TableCell><strong>Reasoning</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Database" size="small" /></TableCell>
                <TableCell><strong style={{ fontSize: 16 }}>{conflict.dbAnswer}</strong></TableCell>
                <TableCell>—</TableCell>
                <TableCell>Stored correct answer</TableCell>
              </TableRow>
              {conflict.primaryAnswer && (
                <TableRow>
                  <TableCell>
                    <Chip label={`Primary: ${conflict.primaryProvider}`} size="small"
                      sx={{ backgroundColor: providerColor[conflict.primaryProvider] || '#666', color: '#fff' }} />
                  </TableCell>
                  <TableCell>
                    <strong style={{ fontSize: 16, color: conflict.primaryAnswer === conflict.dbAnswer ? '#22c55e' : '#dc3545' }}>
                      {conflict.primaryAnswer}
                    </strong>
                  </TableCell>
                  <TableCell>{conflict.primaryConfidence ? (conflict.primaryConfidence * 100).toFixed(0) + '%' : '—'}</TableCell>
                  <TableCell>{conflict.primaryReasoning || '—'}</TableCell>
                </TableRow>
              )}
              {conflict.secondaryAnswer && (
                <TableRow>
                  <TableCell>
                    <Chip label={`Secondary: ${conflict.secondaryProvider}`} size="small"
                      sx={{ backgroundColor: providerColor[conflict.secondaryProvider] || '#666', color: '#fff' }} />
                  </TableCell>
                  <TableCell>
                    <strong style={{ fontSize: 16, color: conflict.secondaryAnswer === conflict.dbAnswer ? '#22c55e' : '#dc3545' }}>
                      {conflict.secondaryAnswer}
                    </strong>
                  </TableCell>
                  <TableCell>{conflict.secondaryConfidence ? (conflict.secondaryConfidence * 100).toFixed(0) + '%' : '—'}</TableCell>
                  <TableCell>{conflict.secondaryReasoning || '—'}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* AI Explanations with "Use This" buttons */}
        {(conflict.primaryExplanation || conflict.secondaryExplanation) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              AI Explanations — click "Use This" to populate the explanation field below
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <ExplanationPanel
                label="Primary"
                provider={conflict.primaryProvider}
                model={conflict.primaryModel}
                confidence={conflict.primaryConfidence}
                explanation={conflict.primaryExplanation}
                selected={selectedSource === 'primary'}
                onUse={() => handleUseExplanation(conflict.primaryExplanation, 'primary', conflict.primaryAnswer)}
              />
              <ExplanationPanel
                label="Secondary"
                provider={conflict.secondaryProvider}
                model={conflict.secondaryModel}
                confidence={conflict.secondaryConfidence}
                explanation={conflict.secondaryExplanation}
                selected={selectedSource === 'secondary'}
                onUse={() => handleUseExplanation(conflict.secondaryExplanation, 'secondary', conflict.secondaryAnswer)}
              />
            </Box>
          </Box>
        )}

        {/* Resolution form */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Select Correct Answer</Typography>
        <RadioGroup row value={resolvedAnswer} onChange={(e) => setResolvedAnswer(e.target.value)}>
          {answerOptions.map((opt) => (
            <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} />
          ))}
        </RadioGroup>

        {/* Final Explanation — rich editor in a panel matching Primary/Secondary style */}
        <Paper variant="outlined" sx={{
          mt: 2, p: 2, flex: 1,
          border: '2px solid #3366FF',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                Final Explanation
              </Typography>
              {selectedSource && (
                <Chip label={`Based on ${selectedSource}`} size="small" color="primary" sx={{ ml: 1, fontSize: 10, height: 18 }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Tooltip title="Edit in fullscreen">
                <IconButton size="small" onClick={() => setExpandFinalExpl(true)}>
                  <OpenInFullIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="textSecondary">
                Edit below to customize
              </Typography>
            </Box>
          </Box>
          <JoditEditor
            ref={editorRef}
            value={resolvedExplanation}
            config={{
              readonly: false,
              askBeforePasteHTML: false,
              askBeforePasteFromWord: false,
              height: 250,
              buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,table,|,undo,redo,|,source',
            }}
            onBlur={(newContent) => {
              const val = typeof newContent === 'string' ? newContent : newContent?.target?.innerHTML || '';
              setResolvedExplanation(val);
              setSelectedSource(null);
            }}
          />
        </Paper>

        {/* Fullscreen editor for Final Explanation */}
        <Dialog open={expandFinalExpl} onClose={() => setExpandFinalExpl(false)} maxWidth="lg" fullWidth disableEnforceFocus>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Final Explanation — Edit</span>
            <IconButton size="small" onClick={() => setExpandFinalExpl(false)}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <JoditEditor
              value={resolvedExplanation}
              config={{
                readonly: false,
                askBeforePasteHTML: false,
                askBeforePasteFromWord: false,
                height: 500,
                buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,table,|,undo,redo,|,source',
              }}
              onBlur={(newContent) => {
                const val = typeof newContent === 'string' ? newContent : newContent?.target?.innerHTML || '';
                setResolvedExplanation(val);
                setSelectedSource(null);
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExpandFinalExpl(false)}>Done</Button>
          </DialogActions>
        </Dialog>

        <TextField
          label="Notes (optional)"
          multiline rows={2} fullWidth sx={{ mt: 2 }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          helperText="Reason for this resolution — recorded in audit log."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleResolve} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Resolve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ─────────────────────────────────────────────────
export default function AnswerConflicts() {
  const canResolve = usePermission('QA Actions', 'Resolve Answer Conflict');
  const canDismiss = usePermission('QA Actions', 'Dismiss Answer Conflict');
  // View state: null = subject cards, object = drilled into a subject
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectStats, setSubjectStats] = useState([]);
  const [subjectStatsLoading, setSubjectStatsLoading] = useState(false);

  // Conflicts table state
  const [conflicts, setConflicts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reviewedByFilter, setReviewedByFilter] = useState('');
  const [activeStatsFilter, setActiveStatsFilter] = useState(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const pageSize = 20;

  const fetchSubjectStats = useCallback(async () => {
    setSubjectStatsLoading(true);
    try {
      const data = await getInstutueApp(apiPaths.answerConflictsStatsBySubject, null, getHeaders());
      if (data?.success) {
        setSubjectStats(
          filterBySubjectScope(data.subjects || [], (s) => s.subject_id)
        );
      }
    } catch (e) {
      console.error('Failed to fetch subject stats', e);
    } finally {
      setSubjectStatsLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await getInstutueApp(apiPaths.answerConflictsStats, null, getHeaders());
      if (data?.success) {
        setStats(data);
      }
    } catch (e) {
      console.error('Failed to fetch stats', e);
    }
  }, []);

  const fetchConflicts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page - 1, pageSize };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.conflictType = typeFilter;
      if (selectedSubject) params.subjectId = selectedSubject.subject_id;
      if (dateFrom) params.fromDate = dateFrom;
      if (dateTo) params.toDate = dateTo;
      if (reviewedByFilter) params.reviewedBy = reviewedByFilter;

      const data = await getInstutueApp(apiPaths.answerConflicts, params, getHeaders());
      if (data?.success) {
        setConflicts(
          filterBySubjectScope(data.conflicts || [], (c) => c.subjectId ?? c.dbSubjectId)
        );
        setTotalPages(data.totalPages || 1);
      }
    } catch (e) {
      toast.error('Failed to load conflicts');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, selectedSubject, dateFrom, dateTo, reviewedByFilter]);

  // Load subject cards on mount
  useEffect(() => { fetchSubjectStats(); fetchStats(); }, [fetchSubjectStats, fetchStats]);
  // Load conflicts when in table view
  useEffect(() => { if (selectedSubject !== null) fetchConflicts(); }, [fetchConflicts, selectedSubject]);

  const handleSubjectClick = (subject) => {
    setSelectedSubject(subject);
    setPage(1);
    setStatusFilter('');
    setTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setReviewedByFilter('');
    setActiveStatsFilter(null);
  };

  const handleBackToSubjects = () => {
    setSelectedSubject(null);
    setConflicts([]);
    setActiveStatsFilter(null);
    fetchSubjectStats(); // refresh counts
  };

  const handleViewAll = () => {
    setSelectedSubject({ subject_id: null, subject_name: 'All Subjects' });
    setPage(1);
    setStatusFilter('');
    setTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setReviewedByFilter('');
    setActiveStatsFilter(null);
  };

  const handleStatsCardClick = (filterKey) => {
    // If clicking the already-active filter, clear it and go back to subject cards
    if (activeStatsFilter === filterKey) {
      setActiveStatsFilter(null);
      setStatusFilter('');
      setTypeFilter('');
      setPage(1);
      setSelectedSubject(null);
      setConflicts([]);
      fetchSubjectStats();
      return;
    }

    setActiveStatsFilter(filterKey);
    setPage(1);

    if (filterKey === 'all') {
      setStatusFilter('');
      setTypeFilter('');
    } else if (filterKey === 'conflicts') {
      // Show all conflicts (non-ALL_AGREE) — filter by PENDING status as conflicts needing attention
      setStatusFilter('PENDING');
      setTypeFilter('');
    } else if (filterKey === 'corrected') {
      // Answers Corrected is a subset of Resolved — filter by RESOLVED status
      setStatusFilter('RESOLVED');
      setTypeFilter('');
    } else if (filterKey.startsWith('status:')) {
      setStatusFilter(filterKey.split(':')[1]);
      setTypeFilter('');
    } else if (filterKey.startsWith('type:')) {
      setTypeFilter(filterKey.split(':')[1]);
      setStatusFilter('');
    }

    // Always switch to "All Subjects" view — stats cards show global counts,
    // so filtering within a specific subject would give misleading results
    setSelectedSubject({ subject_id: null, subject_name: 'All Subjects' });
  };

  const handleResolve = async (conflictId, resolvedAnswer, resolvedExplanation, notes) => {
    const data = await putInstituteAppData(
      apiPaths.answerConflicts + '/' + conflictId + '/resolve',
      { data: { resolvedAnswer, resolvedExplanation, notes }, headers: getHeaders() }
    );
    if (data?.success) {
      toast.success('Conflict resolved' + (data.answerChanged ? ' — answer updated!' : ''));
      fetchConflicts();
      fetchStats();
      fetchSubjectStats();
    } else {
      throw new Error(data?.message || 'Resolution failed');
    }
  };

  const handleDismiss = async (conflictId) => {
    if (!window.confirm('Dismiss this conflict? No changes will be made.')) return;
    try {
      const data = await putInstituteAppData(
        apiPaths.answerConflicts + '/' + conflictId + '/dismiss',
        { data: {}, headers: getHeaders() }
      );
      if (data?.success) {
        toast.info('Conflict dismissed');
        fetchConflicts();
        fetchStats();
        fetchSubjectStats();
      }
    } catch (e) {
      toast.error('Failed to dismiss conflict');
    }
  };

  const handleSwitchExplanation = async (questionId, explanation, source) => {
    if (!explanation?.trim()) {
      throw new Error('Explanation cannot be empty');
    }
    const data = await putInstituteAppData(
      apiPaths.answerConflictsUpdateExplanation + '/' + questionId,
      { data: { explanation, notes: 'Switched to ' + source + ' explanation' }, headers: getHeaders() }
    );
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to update explanation');
    }
  };

  const openResolveDialog = (conflict) => {
    setSelectedConflict(conflict);
    setResolveDialogOpen(true);
  };

  const openDetailDialog = (conflict) => {
    setSelectedConflict(conflict);
    setDetailDialogOpen(true);
  };

  // ─── Subject Card View (default landing) ──────────────────────────
  const renderSubjectCards = () => (
    <>
      {subjectStatsLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : subjectStats.length === 0 ? (
        <Alert severity="info">No verified questions yet. Run the batch verification or verify individual questions to see results here.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {subjectStats.map((s) => {
            const pending = Number(s.pending || 0);
            const total = Number(s.total || 0);
            const allAgree = Number(s.all_agree || 0);
            const hasPending = pending > 0;

            return (
              <Paper key={s.subject_id} elevation={hasPending ? 3 : 1}
                sx={{
                  p: 2, width: 200, cursor: 'pointer',
                  borderTop: hasPending ? '4px solid #dc3545' : '4px solid #22c55e',
                  '&:hover': { boxShadow: 4 },
                  transition: 'box-shadow 0.2s',
                }}
                onClick={() => handleSubjectClick(s)}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {s.subject_name || 'Unknown Subject'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption">
                    Total: <strong>{total}</strong>
                  </Typography>
                  {hasPending && (
                    <Typography variant="caption" sx={{ color: '#dc3545', fontWeight: 'bold' }}>
                      Pending: {pending}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: '#22c55e' }}>
                    Agreed: {allAgree}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </>
  );

  // ─── Conflicts Table View (after subject drill-down) ──────────────
  const renderConflictsTable = () => (
    <>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); setActiveStatsFilter(null); }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(statusConfig).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Conflict Type</InputLabel>
          <Select value={typeFilter} label="Conflict Type" onChange={(e) => { setTypeFilter(e.target.value); setPage(1); setActiveStatsFilter(null); }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(conflictTypeConfig).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small" type="date" label="From Date" value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" type="date" label="To Date" value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" label="Reviewed By" value={reviewedByFilter}
          onChange={(e) => { setReviewedByFilter(e.target.value); setPage(1); }}
          placeholder="Email or name" sx={{ minWidth: 160 }}
        />
        {(dateFrom || dateTo || reviewedByFilter) && (
          <Chip
            label="Clear date/user filters"
            onDelete={() => { setDateFrom(''); setDateTo(''); setReviewedByFilter(''); setPage(1); }}
            size="small" color="primary" variant="outlined"
          />
        )}
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : conflicts.length === 0 ? (
        <Alert severity="info">No conflicts found matching your filters.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Question ID</TableCell>
                  {/* Show subject column only when viewing all subjects */}
                  {!selectedSubject?.subject_id && <TableCell>Subject</TableCell>}
                  <TableCell>DB Answer</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>Secondary</TableCell>
                  <TableCell>Conflict Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conflicts.map((c) => {
                  const typeCfg = conflictTypeConfig[c.conflictType] || {};
                  const statusCfg = statusConfig[c.status] || {};

                  return (
                    <TableRow key={c.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => (c.status === 'PENDING' && canResolve) ? openResolveDialog(c) : openDetailDialog(c)}>
                      <TableCell><strong>#{c.questionId}</strong></TableCell>
                      {!selectedSubject?.subject_id && (
                        <TableCell sx={{ fontSize: 12 }}>{c.subjectName || '—'}</TableCell>
                      )}
                      <TableCell><strong>{c.dbAnswer}</strong></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span style={{
                            color: c.primaryAnswer === c.dbAnswer ? '#22c55e' : '#dc3545',
                            fontWeight: 'bold'
                          }}>{c.primaryAnswer || '—'}</span>
                          {c.primaryProvider && (
                            <Chip label={c.primaryProvider} size="small" sx={{
                              fontSize: 10, height: 18,
                              backgroundColor: providerColor[c.primaryProvider] || '#666', color: '#fff'
                            }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span style={{
                            color: c.secondaryAnswer === c.dbAnswer ? '#22c55e' : '#dc3545',
                            fontWeight: 'bold'
                          }}>{c.secondaryAnswer || '—'}</span>
                          {c.secondaryProvider && (
                            <Chip label={c.secondaryProvider} size="small" sx={{
                              fontSize: 10, height: 18,
                              backgroundColor: providerColor[c.secondaryProvider] || '#666', color: '#fff'
                            }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={typeCfg.icon}
                          label={typeCfg.label || c.conflictType}
                          size="small"
                          sx={{ backgroundColor: typeCfg.color, color: '#fff', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Chip
                            label={statusCfg.label || c.status}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: statusCfg.color, color: statusCfg.color }}
                          />
                          {c.resolvedBy === 'SYSTEM_AUTO_CONTENT_PRESERVED' && (
                            <Chip label="Images/Tables Preserved" size="small" color="warning"
                              sx={{ fontSize: 9, height: 18, fontWeight: 'bold' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={c.source === 'BATCH' ? 'Batch' : 'Manual'}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: c.source === 'BATCH' ? '#8b5cf6' : '#3366FF',
                            color: c.source === 'BATCH' ? '#8b5cf6' : '#3366FF',
                            fontSize: 11
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                          {c.status === 'PENDING' && (
                            <>
                              {canResolve && (
                                <Tooltip title="Resolve">
                                  <IconButton size="small" color="primary" onClick={() => openResolveDialog(c)}>
                                    <ThumbUpIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canDismiss && (
                                <Tooltip title="Dismiss">
                                  <IconButton size="small" color="default" onClick={() => handleDismiss(c.id)}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                          {c.status !== 'PENDING' && (
                            <Tooltip title="View details">
                              <IconButton size="small" onClick={() => openDetailDialog(c)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </>
      )}
    </>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedSubject && (
            <Button size="small" variant="outlined" onClick={handleBackToSubjects}
              sx={{ textTransform: 'none', mr: 1 }}>
              Back to Subjects
            </Button>
          )}
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {selectedSubject ? selectedSubject.subject_name : 'Answer Conflicts'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!selectedSubject && (
            <Button size="small" variant="outlined" onClick={handleViewAll}
              sx={{ textTransform: 'none' }}>
              View All
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={() => {
              fetchStats();
              fetchSubjectStats();
              if (selectedSubject) fetchConflicts();
            }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <StatsDashboard stats={stats} onCardClick={handleStatsCardClick} activeFilter={activeStatsFilter} />

      {selectedSubject === null ? renderSubjectCards() : renderConflictsTable()}

      <ResolveDialog
        open={resolveDialogOpen}
        conflict={selectedConflict}
        onClose={() => setResolveDialogOpen(false)}
        onResolve={handleResolve}
      />

      <DetailDialog
        open={detailDialogOpen}
        conflict={selectedConflict}
        onClose={() => setDetailDialogOpen(false)}
        onSwitchExplanation={handleSwitchExplanation}
        onCorrectAnswer={handleResolve}
        onRefresh={() => { fetchConflicts(); fetchStats(); fetchSubjectStats(); }}
      />
    </Box>
  );
}

// ─── Inline Resolve Panel (no Dialog wrapper — for inline panel swap in QuestionViewer) ──
function ResolvePanel({ conflict, onCancel, onResolve }) {
  const [resolvedAnswer, setResolvedAnswer] = useState('');
  const [resolvedExplanation, setResolvedExplanation] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [expandFinalExpl, setExpandFinalExpl] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (conflict) {
      if (conflict.primaryExplanation) {
        setResolvedExplanation(conflict.primaryExplanation);
        setResolvedAnswer(conflict.primaryAnswer || conflict.dbAnswer || '');
        setSelectedSource('primary');
      } else if (conflict.secondaryExplanation) {
        setResolvedExplanation(conflict.secondaryExplanation);
        setResolvedAnswer(conflict.secondaryAnswer || conflict.dbAnswer || '');
        setSelectedSource('secondary');
      } else {
        setResolvedExplanation('');
        setResolvedAnswer(conflict.dbAnswer || '');
        setSelectedSource(null);
      }
      setNotes('');
    }
  }, [conflict]);

  const handleUseExplanation = (explanation, source, answer) => {
    setResolvedExplanation(explanation);
    setSelectedSource(source);
    if (answer) setResolvedAnswer(answer);
  };

  const handleResolve = async () => {
    if (!resolvedAnswer) {
      toast.error('Please select the correct answer');
      return;
    }
    setSaving(true);
    try {
      await onResolve(conflict.id, resolvedAnswer, resolvedExplanation, notes);
      onCancel();
    } catch (e) {
      toast.error('Failed to resolve: ' + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  if (!conflict) return null;

  const answerOptions = ['A', 'B', 'C', 'D'];

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Resolve Conflict — Question #{conflict.questionId}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={conflict.source === 'BATCH' ? 'Batch' : 'Manual'}
            size="small"
            variant="outlined"
            sx={{
              borderColor: conflict.source === 'BATCH' ? '#8b5cf6' : '#3366FF',
              color: conflict.source === 'BATCH' ? '#8b5cf6' : '#3366FF'
            }}
          />
        </Box>
      </Box>

      {/* Question context */}
      <QuestionPreview questionId={conflict.questionId} />

      {/* Source comparison table */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Answer Comparison</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Source</strong></TableCell>
              <TableCell><strong>Answer</strong></TableCell>
              <TableCell><strong>Confidence</strong></TableCell>
              <TableCell><strong>Reasoning</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><Chip label="Database" size="small" /></TableCell>
              <TableCell><strong style={{ fontSize: 16 }}>{conflict.dbAnswer}</strong></TableCell>
              <TableCell>—</TableCell>
              <TableCell>Stored correct answer</TableCell>
            </TableRow>
            {conflict.primaryAnswer && (
              <TableRow>
                <TableCell>
                  <Chip label={`Primary: ${conflict.primaryProvider}`} size="small"
                    sx={{ backgroundColor: providerColor[conflict.primaryProvider] || '#666', color: '#fff' }} />
                </TableCell>
                <TableCell>
                  <strong style={{ fontSize: 16, color: conflict.primaryAnswer === conflict.dbAnswer ? '#22c55e' : '#dc3545' }}>
                    {conflict.primaryAnswer}
                  </strong>
                </TableCell>
                <TableCell>{conflict.primaryConfidence ? (conflict.primaryConfidence * 100).toFixed(0) + '%' : '—'}</TableCell>
                <TableCell>{conflict.primaryReasoning || '—'}</TableCell>
              </TableRow>
            )}
            {conflict.secondaryAnswer && (
              <TableRow>
                <TableCell>
                  <Chip label={`Secondary: ${conflict.secondaryProvider}`} size="small"
                    sx={{ backgroundColor: providerColor[conflict.secondaryProvider] || '#666', color: '#fff' }} />
                </TableCell>
                <TableCell>
                  <strong style={{ fontSize: 16, color: conflict.secondaryAnswer === conflict.dbAnswer ? '#22c55e' : '#dc3545' }}>
                    {conflict.secondaryAnswer}
                  </strong>
                </TableCell>
                <TableCell>{conflict.secondaryConfidence ? (conflict.secondaryConfidence * 100).toFixed(0) + '%' : '—'}</TableCell>
                <TableCell>{conflict.secondaryReasoning || '—'}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* AI Explanations with "Use This" buttons */}
      {(conflict.primaryExplanation || conflict.secondaryExplanation) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            AI Explanations — click "Use This" to populate the explanation field below
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ExplanationPanel
              label="Primary"
              provider={conflict.primaryProvider}
              model={conflict.primaryModel}
              confidence={conflict.primaryConfidence}
              explanation={conflict.primaryExplanation}
              selected={selectedSource === 'primary'}
              onUse={() => handleUseExplanation(conflict.primaryExplanation, 'primary', conflict.primaryAnswer)}
            />
            <ExplanationPanel
              label="Secondary"
              provider={conflict.secondaryProvider}
              model={conflict.secondaryModel}
              confidence={conflict.secondaryConfidence}
              explanation={conflict.secondaryExplanation}
              selected={selectedSource === 'secondary'}
              onUse={() => handleUseExplanation(conflict.secondaryExplanation, 'secondary', conflict.secondaryAnswer)}
            />
          </Box>
        </Box>
      )}

      {/* Resolution form */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Select Correct Answer</Typography>
      <RadioGroup row value={resolvedAnswer} onChange={(e) => setResolvedAnswer(e.target.value)}>
        {answerOptions.map((opt) => (
          <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} />
        ))}
      </RadioGroup>

      {/* Final Explanation — rich editor */}
      <Paper variant="outlined" sx={{ mt: 2, p: 2, border: '2px solid #3366FF' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
              Final Explanation
            </Typography>
            {selectedSource && (
              <Chip label={`Based on ${selectedSource}`} size="small" color="primary" sx={{ ml: 1, fontSize: 10, height: 18 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Edit in fullscreen">
              <IconButton size="small" onClick={() => setExpandFinalExpl(true)}>
                <OpenInFullIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color="textSecondary">
              Edit below to customize
            </Typography>
          </Box>
        </Box>
        <JoditEditor
          ref={editorRef}
          value={resolvedExplanation}
          config={{
            readonly: false,
            askBeforePasteHTML: false,
            askBeforePasteFromWord: false,
            height: 250,
            buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,table,|,undo,redo,|,source',
          }}
          onBlur={(newContent) => {
            const val = typeof newContent === 'string' ? newContent : newContent?.target?.innerHTML || '';
            setResolvedExplanation(val);
            setSelectedSource(null);
          }}
        />
      </Paper>

      {/* Fullscreen editor for Final Explanation */}
      <Dialog open={expandFinalExpl} onClose={() => setExpandFinalExpl(false)} maxWidth="lg" fullWidth disableEnforceFocus>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Final Explanation — Edit</span>
          <IconButton size="small" onClick={() => setExpandFinalExpl(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <JoditEditor
            value={resolvedExplanation}
            config={{
              readonly: false,
              askBeforePasteHTML: false,
              askBeforePasteFromWord: false,
              height: 500,
              buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,table,|,undo,redo,|,source',
            }}
            onBlur={(newContent) => {
              const val = typeof newContent === 'string' ? newContent : newContent?.target?.innerHTML || '';
              setResolvedExplanation(val);
              setSelectedSource(null);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpandFinalExpl(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <TextField
        label="Notes (optional)"
        multiline rows={2} fullWidth sx={{ mt: 2 }}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        helperText="Reason for this resolution — recorded in audit log."
      />

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleResolve} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Resolve'}
        </Button>
      </Box>
    </Box>
  );
}

// ─── Inline Detail Panel (no Dialog wrapper — for inline panel swap in QuestionViewer) ──
function DetailPanel({ conflict, onCancel, onSwitchExplanation, onCorrectAnswer, onRefresh }) {
  const canUpdateExplanation = usePermission('QA Actions', 'Update Explanation');
  const [switching, setSwitching] = useState(false);
  const [activeExplanation, setActiveExplanation] = useState(null);
  const [editedExplanation, setEditedExplanation] = useState('');
  const [selectedSource, setSelectedSource] = useState(null);
  const [showAnswerCorrection, setShowAnswerCorrection] = useState(false);
  const [correctedAnswer, setCorrectedAnswer] = useState('');
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [correcting, setCorrecting] = useState(false);
  const [expandFinalExpl, setExpandFinalExpl] = useState(false);
  const detailEditorRef = useRef(null);

  useEffect(() => {
    if (conflict) {
      setActiveExplanation(conflict.resolvedExplanation);
      setEditedExplanation(conflict.resolvedExplanation || '');
      setSelectedSource(null);
      setCorrectedAnswer(conflict.resolvedAnswer || conflict.dbAnswer || '');
      setShowAnswerCorrection(false);
      setCorrectionNotes('');
    }
  }, [conflict]);

  if (!conflict) return null;

  const isResolved = conflict.status === 'RESOLVED';
  const currentAnswer = conflict.resolvedAnswer || conflict.dbAnswer;

  const handleUseExplanation = (explanation, source, answer) => {
    setEditedExplanation(explanation);
    setSelectedSource(source);
    if (answer) setCorrectedAnswer(answer);
  };

  const handleSaveExplanation = async () => {
    if (!editedExplanation?.trim()) {
      toast.error('Explanation cannot be empty');
      return;
    }
    setSwitching(true);
    try {
      const answerChanged = correctedAnswer && correctedAnswer !== currentAnswer;
      if (answerChanged) {
        await onCorrectAnswer(conflict.id, correctedAnswer, editedExplanation, 'Answer and explanation updated');
        toast.success('Answer changed to ' + correctedAnswer + ' and explanation updated');
      } else {
        await onSwitchExplanation(conflict.questionId, editedExplanation, selectedSource || 'edited');
        toast.success('Explanation updated');
      }
      setActiveExplanation(editedExplanation);
      setSelectedSource(null);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Failed to save: ' + (e.message || e));
    } finally {
      setSwitching(false);
    }
  };

  const handleCorrectAnswer = async () => {
    if (!correctedAnswer) {
      toast.error('Please select the correct answer');
      return;
    }
    if (correctedAnswer === currentAnswer) {
      toast.info('Same answer selected — no change needed');
      setShowAnswerCorrection(false);
      return;
    }
    setCorrecting(true);
    try {
      await onCorrectAnswer(conflict.id, correctedAnswer, activeExplanation || '', correctionNotes);
      toast.success('Answer corrected from ' + currentAnswer + ' to ' + correctedAnswer);
      setShowAnswerCorrection(false);
      if (onRefresh) onRefresh();
      onCancel();
    } catch (e) {
      toast.error('Failed to correct answer: ' + (e.message || e));
    } finally {
      setCorrecting(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Verification Details — Question #{conflict.questionId}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {conflictTypeConfig[conflict.conflictType] && (
            <Chip
              icon={conflictTypeConfig[conflict.conflictType].icon}
              label={conflictTypeConfig[conflict.conflictType].label}
              size="small"
              sx={{ backgroundColor: conflictTypeConfig[conflict.conflictType].color, color: '#fff' }}
            />
          )}
          {statusConfig[conflict.status] && (
            <Chip label={statusConfig[conflict.status].label} size="small" variant="outlined"
              sx={{ borderColor: statusConfig[conflict.status].color, color: statusConfig[conflict.status].color }} />
          )}
          <Chip
            label={conflict.source === 'BATCH' ? 'Batch' : 'Manual'}
            size="small"
            variant="outlined"
            sx={{
              borderColor: conflict.source === 'BATCH' ? '#8b5cf6' : '#3366FF',
              color: conflict.source === 'BATCH' ? '#8b5cf6' : '#3366FF'
            }}
          />
        </Box>
      </Box>

      {/* Question context */}
      <QuestionPreview questionId={conflict.questionId} />

      {/* Answer comparison */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Answer Comparison</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Source</strong></TableCell>
              <TableCell><strong>Answer</strong></TableCell>
              <TableCell><strong>Confidence</strong></TableCell>
              <TableCell><strong>Reasoning</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><Chip label="Database" size="small" /></TableCell>
              <TableCell><strong style={{ fontSize: 16 }}>{conflict.dbAnswer}</strong></TableCell>
              <TableCell>—</TableCell>
              <TableCell>Stored correct answer</TableCell>
            </TableRow>
            {conflict.primaryAnswer && (
              <TableRow>
                <TableCell>
                  <Chip label={`Primary: ${conflict.primaryProvider}`} size="small"
                    sx={{ backgroundColor: providerColor[conflict.primaryProvider] || '#666', color: '#fff' }} />
                </TableCell>
                <TableCell>
                  <strong style={{ fontSize: 16, color: conflict.primaryAnswer === conflict.dbAnswer ? '#22c55e' : '#dc3545' }}>
                    {conflict.primaryAnswer}
                  </strong>
                </TableCell>
                <TableCell>{conflict.primaryConfidence ? (conflict.primaryConfidence * 100).toFixed(0) + '%' : '—'}</TableCell>
                <TableCell>{conflict.primaryReasoning || '—'}</TableCell>
              </TableRow>
            )}
            {conflict.secondaryAnswer && (
              <TableRow>
                <TableCell>
                  <Chip label={`Secondary: ${conflict.secondaryProvider}`} size="small"
                    sx={{ backgroundColor: providerColor[conflict.secondaryProvider] || '#666', color: '#fff' }} />
                </TableCell>
                <TableCell>
                  <strong style={{ fontSize: 16, color: conflict.secondaryAnswer === conflict.dbAnswer ? '#22c55e' : '#dc3545' }}>
                    {conflict.secondaryAnswer}
                  </strong>
                </TableCell>
                <TableCell>{conflict.secondaryConfidence ? (conflict.secondaryConfidence * 100).toFixed(0) + '%' : '—'}</TableCell>
                <TableCell>{conflict.secondaryReasoning || '—'}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Explanations with "Use This" buttons for resolved records */}
      {(conflict.primaryExplanation || conflict.secondaryExplanation) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            AI Explanations {isResolved && '— click "Use This" to load into the editor below'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ExplanationPanel
              label="Primary"
              provider={conflict.primaryProvider}
              model={conflict.primaryModel}
              confidence={conflict.primaryConfidence}
              explanation={conflict.primaryExplanation}
              selected={selectedSource === 'Primary'}
              onUse={isResolved ? () => handleUseExplanation(conflict.primaryExplanation, 'Primary', conflict.primaryAnswer) : null}
            />
            <ExplanationPanel
              label="Secondary"
              provider={conflict.secondaryProvider}
              model={conflict.secondaryModel}
              confidence={conflict.secondaryConfidence}
              explanation={conflict.secondaryExplanation}
              selected={selectedSource === 'Secondary'}
              onUse={isResolved ? () => handleUseExplanation(conflict.secondaryExplanation, 'Secondary', conflict.secondaryAnswer) : null}
            />
          </Box>
        </Box>
      )}

      {/* Select Correct Answer — always visible for resolved records */}
      {isResolved && canUpdateExplanation && (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Select Correct Answer
            <span style={{ fontWeight: 'normal', marginLeft: 8, color: '#888', fontSize: 12 }}>
              (DB: {conflict.dbAnswer}
              {conflict.primaryAnswer && ` | Primary: ${conflict.primaryAnswer}`}
              {conflict.secondaryAnswer && ` | Secondary: ${conflict.secondaryAnswer}`})
            </span>
          </Typography>
          <RadioGroup row value={correctedAnswer} onChange={(e) => setCorrectedAnswer(e.target.value)}>
            {['A', 'B', 'C', 'D'].map((opt) => (
              <FormControlLabel key={opt} value={opt} control={<Radio size="small" />}
                label={<span style={{ fontWeight: opt === correctedAnswer ? 'bold' : 'normal' }}>
                  {opt} {opt === currentAnswer && '(current)'}
                </span>}
              />
            ))}
          </RadioGroup>
        </Box>
      )}

      {/* Editable final explanation */}
      {isResolved && canUpdateExplanation && (
        <Paper variant="outlined" sx={{ mt: 1, mb: 2, p: 2, border: '2px solid #3366FF' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                Final Explanation
              </Typography>
              {selectedSource && (
                <Chip label={`Based on ${selectedSource}`} size="small" color="primary" sx={{ ml: 1, fontSize: 10, height: 18 }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Tooltip title="Edit in fullscreen">
                <IconButton size="small" onClick={() => setExpandFinalExpl(true)}>
                  <OpenInFullIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Button size="small" variant="contained" onClick={handleSaveExplanation} disabled={switching}
                sx={{ textTransform: 'none' }}>
                {switching ? <CircularProgress size={16} /> : 'Save Explanation'}
              </Button>
            </Box>
          </Box>
          <JoditEditor
            ref={detailEditorRef}
            value={editedExplanation}
            config={{
              readonly: false,
              askBeforePasteHTML: false,
              askBeforePasteFromWord: false,
              height: 250,
              buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,table,|,undo,redo,|,source',
            }}
            onBlur={(newContent) => {
              const val = typeof newContent === 'string' ? newContent : newContent?.target?.innerHTML || '';
              setEditedExplanation(val);
              setSelectedSource(null);
            }}
          />
        </Paper>
      )}

      {/* Fullscreen editor for Final Explanation */}
      <Dialog open={expandFinalExpl} onClose={() => setExpandFinalExpl(false)} maxWidth="lg" fullWidth disableEnforceFocus>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Final Explanation — Edit</span>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button size="small" variant="contained" sx={{ textTransform: 'none' }}
              onClick={() => { handleSaveExplanation(); setExpandFinalExpl(false); }}
              disabled={switching || !editedExplanation?.trim()}>
              {switching ? <CircularProgress size={16} /> : 'Save Explanation'}
            </Button>
            <IconButton size="small" onClick={() => setExpandFinalExpl(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <JoditEditor
            value={editedExplanation}
            config={{
              readonly: false,
              askBeforePasteHTML: false,
              askBeforePasteFromWord: false,
              height: 500,
              buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,table,|,undo,redo,|,source',
            }}
            onBlur={(newContent) => {
              const val = typeof newContent === 'string' ? newContent : newContent?.target?.innerHTML || '';
              setEditedExplanation(val);
              setSelectedSource(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Resolution info */}
      {isResolved && conflict.resolvedBy && (
        <Alert severity={conflict.resolvedBy === 'SYSTEM_AUTO_CONTENT_PRESERVED' ? 'warning' : 'info'} sx={{ mt: 1 }}>
          Resolved by <strong>{conflict.resolvedBy}</strong> with answer <strong>{currentAnswer}</strong>
          {conflict.resolvedAt && ` on ${new Date(conflict.resolvedAt).toLocaleString()}`}
          {conflict.source && <> — Source: <strong>{conflict.source === 'BATCH' ? 'Batch Processing' : 'Manual Trigger'}</strong></>}
          {conflict.resolvedBy === 'SYSTEM_AUTO_CONTENT_PRESERVED' && (
            <Chip label="Images/Tables Preserved" size="small" color="warning"
              sx={{ ml: 1, fontSize: 10, height: 20, fontWeight: 'bold' }} />
          )}
        </Alert>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        {switching && <CircularProgress size={20} sx={{ mr: 1 }} />}
        <Button onClick={onCancel}>Back</Button>
      </Box>
    </Box>
  );
}

// Named exports for reuse in QuestionViewer and other pages
export { ResolveDialog, DetailDialog, ResolvePanel, DetailPanel, conflictTypeConfig, statusConfig, getHeaders };
